import React, { useRef, useState, useEffect, useContext, useCallback } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import axios from 'axios';
import { BarrioContext } from '../components/BarrioContext';
import { useTerminologia } from '../hooks/useTerminologia';

const emptyForm = {
    paqueteId: 0,
    nroPedido: '',
    origenEnvioId: null,
    unidadFuncionalId: null,
    destinatario: '',
    encargadoRecibeId: null,
    fechaRecepcion: new Date(),
    observaciones: '',
};

const emptyEntregaForm = {
    paqueteId: 0,
    encargadoEntregaId: 0,
    nombreReceptor: '',
    unidadFuncionalId: 0,
    nroPedido: '',
    destinatario: '',
};

const Paquetes = () => {
    const toast    = useRef(null);
    const HOST_API = process.env.REACT_APP_URL;
    const { barrio, seleccionarBarrio } = useContext(BarrioContext);
    const t = useTerminologia();

    const userData   = JSON.parse(localStorage.getItem('user') || '{}');
    const usuarioId  = userData.id || userData.usuarioId || 0;
    const administracionId = parseInt(localStorage.getItem('administracionId') || '1');

    const [paquetes,       setPaquetes]       = useState([]);
    const [origenes,       setOrigenes]       = useState([]);
    const [porteros,       setPorteros]       = useState([]);
    const [ufs,            setUfs]            = useState([]);
    const [loading,        setLoading]        = useState(false);
    const [visible,        setVisible]        = useState(false);
    const [entregaVisible, setEntregaVisible] = useState(false);
    const [formData,       setFormData]       = useState(emptyForm);
    const [entregaForm,    setEntregaForm]    = useState(emptyEntregaForm);
    const [soloEntregados, setSoloEntregados] = useState(false);

    // ─── Auto-selección de barrio (portero entra directo, sin pasar por PortalBarrio) ──

    useEffect(() => {
        if (barrio?.id) return;
        const barriosPermitidos = JSON.parse(localStorage.getItem('barriosPermitidos') || '[]');
        if (barriosPermitidos.length === 0) return;
        axios.get(`${HOST_API}api/barrios/GetBarrios`, {
            params: { barrioId: barriosPermitidos[0], administracionId }
        }).then(({ data }) => {
            const lista = Array.isArray(data) ? data : [data];
            const b = lista[0];
            if (b) seleccionarBarrio(b.barrioId, b.nombreBarrio, b.cuit, b.direccion, b.razonSocial, b.numeroCuenta, b.cuentaBancaria, b.tipoCuentaBancaria, b.alias);
        }).catch(() => {});
    }, []);

    // ─── Fetch ────────────────────────────────────────────────────────────────────

    const fetchPaquetes = useCallback(async () => {
        if (!barrio?.id) return;
        setLoading(true);
        try {
            const { data } = await axios.get(
                `${HOST_API}api/paquetes/GetPaquetes?administracionId=${administracionId}&barrioId=${barrio.id}&soloEntregados=${soloEntregados}`
            );
            setPaquetes(data);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar los paquetes', life: 3000 });
        } finally {
            setLoading(false);
        }
    }, [HOST_API, barrio?.id, soloEntregados, administracionId]);

    useEffect(() => { fetchPaquetes(); }, [fetchPaquetes]);

    useEffect(() => {
        if (!barrio?.id) return;
        axios.get(`${HOST_API}api/origenenvio/GetOrigenesEnvio`).then(r => setOrigenes(r.data)).catch(() => {});
        axios.get(`${HOST_API}api/paquetes/GetPorterosByBarrio?barrioId=${barrio.id}`).then(r => setPorteros(r.data)).catch(() => {});
        axios.get(`${HOST_API}api/unidadesFuncionales/getUnidadesFuncionales?unidadFuncionalId=0&administracionId=${administracionId}&barrioId=${barrio.id}`).then(r => setUfs(r.data)).catch(() => {});
    }, [HOST_API, barrio?.id, administracionId]);

    // ─── Handlers ─────────────────────────────────────────────────────────────────

    const openNuevo = () => {
        setFormData({ ...emptyForm, encargadoRecibeId: usuarioId, fechaRecepcion: new Date() });
        setVisible(true);
    };

    const handleEdit = (row) => {
        setFormData({
            paqueteId:         row.paqueteId,
            nroPedido:         row.nroPedido || '',
            origenEnvioId:     row.origenEnvioId,
            unidadFuncionalId: row.unidadFuncionalId,
            destinatario:      row.destinatario || '',
            encargadoRecibeId: row.encargadoRecibeId,
            fechaRecepcion:    row.fechaRecepcion ? new Date(row.fechaRecepcion) : new Date(),
            observaciones:     row.observaciones || '',
        });
        setVisible(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.nroPedido.trim() || !formData.origenEnvioId || !formData.unidadFuncionalId || !formData.destinatario.trim() || !formData.encargadoRecibeId) {
            toast.current.show({ severity: 'warn', summary: 'Atención', detail: 'Completá los campos obligatorios', life: 3000 });
            return;
        }
        try {
            await axios.post(`${HOST_API}api/paquetes/SavePaquete`, {
                ...formData,
                administracionId,
                barrioId: barrio.id,
            });
            setVisible(false);
            fetchPaquetes();
            toast.current.show({ severity: 'success', summary: 'Guardado', detail: 'Paquete registrado correctamente', life: 2500 });
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo guardar el paquete', life: 3000 });
        }
    };

    const openEntrega = (row) => {
        setEntregaForm({
            paqueteId:          row.paqueteId,
            encargadoEntregaId: usuarioId,
            nombreReceptor:     '',
            unidadFuncionalId:  row.unidadFuncionalId,
            nroPedido:          row.nroPedido,
            destinatario:       row.destinatario,
        });
        setEntregaVisible(true);
    };

    const handleEntrega = async (e) => {
        e.preventDefault();
        if (!entregaForm.nombreReceptor.trim()) {
            toast.current.show({ severity: 'warn', summary: 'Atención', detail: 'Indicá quién recibió el paquete', life: 3000 });
            return;
        }
        try {
            await axios.post(`${HOST_API}api/paquetes/EntregarPaquete`, entregaForm);
            setEntregaVisible(false);
            fetchPaquetes();
            toast.current.show({ severity: 'success', summary: 'Entregado', detail: 'Paquete marcado como entregado', life: 2500 });
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo registrar la entrega', life: 3000 });
        }
    };

    const handleDelete = (paqueteId, nroPedido) => {
        confirmDialog({
            message: `¿Eliminar el paquete "${nroPedido}"?`,
            header: 'Confirmar eliminación',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Eliminar',
            rejectLabel: 'Cancelar',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await axios.delete(`${HOST_API}api/paquetes/DeletePaquete?paqueteId=${paqueteId}`);
                    fetchPaquetes();
                    toast.current.show({ severity: 'success', summary: 'Eliminado', detail: 'Paquete eliminado', life: 2500 });
                } catch {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo eliminar', life: 3000 });
                }
            },
        });
    };

    // ─── Render helpers ───────────────────────────────────────────────────────────

    const fmtFecha = (row) => row.fechaRecepcion
        ? new Date(row.fechaRecepcion).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' })
        : '';

    const estadoBadge = (row) => {
        const s = row.entregado
            ? { bg: '#ecfdf3', color: '#12b76a', border: '#a9efc5', label: 'Entregado' }
            : { bg: '#fef3f2', color: '#f04438', border: '#fecdca', label: 'Pendiente' };
        return (
            <span style={{
                background: s.bg, color: s.color, border: `1px solid ${s.border}`,
                fontWeight: 600, fontSize: '0.75rem', padding: '0.15rem 0.6rem', borderRadius: 99,
            }}>
                {s.label}
            </span>
        );
    };

    const acciones = (row) => (
        <div className="flex gap-2 justify-content-center">
            {!row.entregado && (
                <>
                    <Button icon="pi pi-check-circle" rounded text severity="success" tooltip="Marcar entregado" onClick={() => openEntrega(row)} />
                    <Button icon="pi pi-pencil"       rounded text severity="warning" tooltip="Editar"           onClick={() => handleEdit(row)} />
                </>
            )}
            <Button icon="pi pi-trash" rounded className="btn-delete" tooltip="Eliminar" onClick={() => handleDelete(row.paqueteId, row.nroPedido)} />
        </div>
    );

    // ─── Render ───────────────────────────────────────────────────────────────────

    return (
        <>
            <h4>Paquetes</h4>
            <br />
            <ConfirmDialog />
            <Toast ref={toast} />
            {loading && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ProgressSpinner style={{ width: 52, height: 52 }} strokeWidth="4" />
                </div>
            )}

            {/* ─── Toolbar ─────────────────────────────────────────────────────── */}
            <div className="flex align-items-center gap-3 mb-3 flex-wrap">
                <Button label="Registrar Paquete" icon="pi pi-plus" onClick={openNuevo} />
                <div className="flex align-items-center gap-2 ml-auto">
                    <Button
                        label={soloEntregados ? 'Ver pendientes' : 'Ver entregados'}
                        icon={soloEntregados ? 'pi pi-clock' : 'pi pi-check'}
                        severity="secondary"
                        outlined
                        className="p-button-sm"
                        onClick={() => setSoloEntregados(v => !v)}
                    />
                </div>
            </div>

            {/* ─── Tabla ───────────────────────────────────────────────────────── */}
            <div className="grid">
                <div className="col-12">
                    <DataTable
                        value={paquetes}
                        paginator
                        rows={15}
                        rowsPerPageOptions={[10, 15, 25, 50]}
                        showGridlines
                        responsiveLayout="scroll"
                        emptyMessage="No hay paquetes"
                        className="p-datatable-gridlines bg-white"
                        sortField="fechaRecepcion"
                        sortOrder={-1}
                    >
                        <Column field="nroPedido"      header="Nro. Pedido"  sortable style={{ fontWeight: 500 }} />
                        <Column field="origenEnvio"    header="Origen"       sortable style={{ width: '10rem' }} />
                        <Column field="codigoUF"       header={t.UF}           sortable style={{ width: '6rem' }} />
                        <Column field="destinatario"   header="Destinatario" sortable />
                        <Column field="encargadoRecibe" header="Recibió"     sortable style={{ width: '12rem' }} />
                        <Column field="fechaRecepcion" header="Fecha"        sortable body={fmtFecha} style={{ width: '11rem' }} />
                        <Column header="Estado" style={{ width: '8rem' }} body={estadoBadge} />
                        <Column header="Acciones" style={{ width: '9rem' }} body={acciones} />
                    </DataTable>
                </div>
            </div>

            {/* ─── Modal registro ──────────────────────────────────────────────── */}
            <Dialog
                header={formData.paqueteId ? 'Editar Paquete' : 'Registrar Paquete'}
                visible={visible}
                style={{ width: '560px' }}
                breakpoints={{ '960px': '100vw' }}
                modal
                onHide={() => setVisible(false)}
            >
                <form onSubmit={handleSubmit}>
                    <div className="grid mt-1">
                        <div className="col-12 md:col-6">
                            <label style={lbl}>Nro. de Pedido <span style={{ color: '#f04438' }}>*</span></label>
                            <InputText
                                value={formData.nroPedido}
                                onChange={(e) => setFormData(p => ({ ...p, nroPedido: e.target.value }))}
                                className="p-inputtext-sm w-full"
                                placeholder="Ej: 12345678"
                                maxLength={100}
                                autoFocus
                            />
                        </div>
                        <div className="col-12 md:col-6">
                            <label style={lbl}>Origen <span style={{ color: '#f04438' }}>*</span></label>
                            <Dropdown
                                value={formData.origenEnvioId}
                                options={origenes}
                                optionLabel="descripcion"
                                optionValue="origenEnvioId"
                                onChange={(e) => setFormData(p => ({ ...p, origenEnvioId: e.value }))}
                                placeholder="Seleccioná origen"
                                filter
                                className="p-inputtext-sm w-full"
                            />
                        </div>
                        <div className="col-12 md:col-6">
                            <label style={lbl}>{t.UnidadFuncional} <span style={{ color: '#f04438' }}>*</span></label>
                            <Dropdown
                                value={formData.unidadFuncionalId}
                                options={ufs}
                                optionLabel="unidadFuncional"
                                optionValue="unidadFuncionalId"
                                onChange={(e) => setFormData(p => ({ ...p, unidadFuncionalId: e.value }))}
                                placeholder="Seleccioná UF"
                                filter
                                className="p-inputtext-sm w-full"
                            />
                        </div>
                        <div className="col-12 md:col-6">
                            <label style={lbl}>Destinatario <span style={{ color: '#f04438' }}>*</span></label>
                            <InputText
                                value={formData.destinatario}
                                onChange={(e) => setFormData(p => ({ ...p, destinatario: e.target.value }))}
                                className="p-inputtext-sm w-full"
                                placeholder="Nombre del destinatario"
                                maxLength={200}
                            />
                        </div>
                        <div className="col-12 md:col-6">
                            <label style={lbl}>Encargado que recibe <span style={{ color: '#f04438' }}>*</span></label>
                            <Dropdown
                                value={formData.encargadoRecibeId}
                                options={porteros}
                                optionLabel="nombreCompleto"
                                optionValue="usuarioId"
                                onChange={(e) => setFormData(p => ({ ...p, encargadoRecibeId: e.value }))}
                                placeholder="Seleccioná encargado"
                                filter
                                className="p-inputtext-sm w-full"
                            />
                        </div>
                        <div className="col-12 md:col-6">
                            <label style={lbl}>Fecha y hora de recepción</label>
                            <Calendar
                                value={formData.fechaRecepcion}
                                onChange={(e) => setFormData(p => ({ ...p, fechaRecepcion: e.value }))}
                                showTime
                                hourFormat="24"
                                dateFormat="dd/mm/yy"
                                showIcon
                                className="p-inputtext-sm w-full"
                            />
                        </div>
                        <div className="col-12">
                            <label style={lbl}>Observaciones</label>
                            <InputTextarea
                                value={formData.observaciones}
                                onChange={(e) => setFormData(p => ({ ...p, observaciones: e.target.value }))}
                                rows={2}
                                className="p-inputtext-sm w-full"
                                placeholder="Observaciones adicionales..."
                                maxLength={500}
                            />
                        </div>
                        <div className="col-12 flex justify-content-end gap-2 mt-2">
                            <Button label="Cancelar" type="button" severity="secondary" text onClick={() => setVisible(false)} />
                            <Button label="Guardar" icon="pi pi-save" type="submit" />
                        </div>
                    </div>
                </form>
            </Dialog>

            {/* ─── Modal entrega ────────────────────────────────────────────────── */}
            <Dialog
                header="Registrar Entrega"
                visible={entregaVisible}
                style={{ width: '400px' }}
                breakpoints={{ '960px': '100vw' }}
                modal
                onHide={() => setEntregaVisible(false)}
            >
                <form onSubmit={handleEntrega}>
                    <div className="flex flex-column gap-3 mt-2">
                        <p style={{ margin: 0, color: '#555', fontSize: '0.9rem' }}>
                            Paquete: <strong>{entregaForm.nroPedido}</strong> — Destinatario: <strong>{entregaForm.destinatario}</strong>
                        </p>
                        <div>
                            <label style={lbl}>Nombre de quien retira <span style={{ color: '#f04438' }}>*</span></label>
                            <InputText
                                value={entregaForm.nombreReceptor}
                                onChange={(e) => setEntregaForm(p => ({ ...p, nombreReceptor: e.target.value }))}
                                className="p-inputtext-sm w-full"
                                placeholder="Ej: Juan Pérez"
                                maxLength={200}
                                autoFocus
                            />
                        </div>
                        <div className="flex justify-content-end gap-2 mt-2">
                            <Button label="Cancelar" type="button" severity="secondary" text onClick={() => setEntregaVisible(false)} />
                            <Button label="Confirmar Entrega" icon="pi pi-check" type="submit" severity="success" />
                        </div>
                    </div>
                </form>
            </Dialog>
        </>
    );
};

const lbl = { display: 'block', marginBottom: '0.35rem', fontWeight: 500, color: '#344054', fontSize: '0.87rem' };

export default Paquetes;
